/* eslint-disable complexity */
import { AnalysisResponse } from '@futrli-sdk/predict_analysis';
import { IconType } from 'carbon-react/lib/components/icon';
import { IntlShape } from 'react-intl';

import { formatMetricValue, getKpiLabel } from './format-utils';
import {
  mapHealthScore,
  mapRevenueChart,
  mapCostStructure,
  mapLiquidityPanel,
  mapInsights,
  mapExecutiveSummary
} from './shared-mappers';
import { MappedBusinessOwnerData, MappedHighlightMetric } from './types';

const KPI_ICONS: Record<string, IconType> = {
  net_profit_margin: 'coins',
  revenue_growth: 'chart_line',
  net_assets: 'bank',
  bank_accounts_balance: 'bank',
  gross_margin_ratio: 'coins',
  working_capital: 'bank'
};

const KPI_COLORS: Record<string, 'positive' | 'warning' | 'negative'> = {
  green: 'positive',
  yellow: 'warning',
  red: 'negative'
};

export const mapToBusinessOwnerData = (analysis: AnalysisResponse['analysis'], intl: IntlShape): MappedBusinessOwnerData => {
  const { currency_code } = analysis;

  const highlightMetrics: MappedHighlightMetric[] = (analysis.kpi_banner_cards ?? []).map(card => ({
    icon: KPI_ICONS[card.kpi_key] ?? 'chart_line',
    label: getKpiLabel(card.kpi_key, 'business_owner', intl),
    value: formatMetricValue(card.value, card.format, currency_code, true),
    description: `${card.period}${card.is_forecast ? ` ${intl.formatMessage({ id: 'aiInsights.kpi.forecast', defaultMessage: 'forecast' })}` : ''}`,
    color: KPI_COLORS[card.status] ?? 'positive'
  }));

  const healthScore = analysis.health_score ? mapHealthScore(analysis.health_score, intl) : undefined;

  const insights = mapInsights(
    analysis.insights,
    analysis.health_score?.score_components,
    currency_code,
    intl
  );

  return {
    analysisId: analysis.id,
    analysisVersion: analysis.version,
    createdAt: analysis.created,
    healthScore,
    highlightMetrics,
    revenueChart: analysis.revenue_chart
      ? mapRevenueChart(
        analysis.revenue_chart,
        currency_code,
        intl.formatMessage({ id: 'aiInsights.businessOwner.salesOverTime', defaultMessage: 'Your sales over time' }),
        intl.formatMessage({ id: 'aiInsights.businessOwner.salesOverTimeSubtitle', defaultMessage: 'Actual results and what the forecast predicts' })
      )
      : undefined,
    costStructure: analysis.cost_structure
      ? mapCostStructure(
        analysis.cost_structure,
        currency_code,
        intl.formatMessage({ id: 'aiInsights.businessOwner.whereMoneyGoes', defaultMessage: 'Where your money goes' }),
        intl.formatMessage({ id: 'aiInsights.businessOwner.costTotalLabel', defaultMessage: 'per year in running costs' }),
        intl.formatMessage({ id: 'aiInsights.businessOwner.costSubtitle', defaultMessage: 'Your annual running costs (not including stock)' })
      )
      : undefined,
    liquidity: analysis.liquidity_panel
      ? mapLiquidityPanel(
        analysis.liquidity_panel,
        currency_code,
        intl.formatMessage({ id: 'aiInsights.businessOwner.cashPosition', defaultMessage: 'Your cash position' }),
        'business_owner',
        intl,
        intl.formatMessage({ id: 'aiInsights.businessOwner.cashPositionSubtitle', defaultMessage: "How much you've got and where it's heading" })
      )
      : undefined,
    insights,
    executiveSummary: mapExecutiveSummary(
      analysis.executive_summary,
      intl.formatMessage({ id: 'aiInsights.businessOwner.theBigPicture', defaultMessage: 'The Big Picture' }),
      intl
    )
  };
};
